/**
 * AnalysisHub Component
 * 
 * Cross-session analytics and visualization dashboard with Drawer + Vertical Tabs navigation.
 * Provides professional analytics interface for reviewing trading performance across sessions,
 * behavioral pattern analysis, and AI-powered insights.
 */

// 1. External dependencies
import React, { useState, useCallback, useRef } from 'react';
import { Pin, PinOff } from 'lucide-react';

// 2. Internal absolute imports
import { 
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Tabs,
  Tab,
  Button,
  useDisclosure,
  ScrollShadowRef
} from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { AppSyncStatus } from '@/components/shared/sync';
import { useIsDesktop } from '@/hooks';
import { AnalysisLayout } from './AnalysisLayout';
import { AnalysisPlaceholder } from './AnalysisPlaceholder';

type AnalysisSection = 
  | 'overview'
  | 'sessions' 
  | 'trades'
  | 'charts'
  | 'ai-insights'
  | 'reports';

/**
 * Main Analysis Hub Component
 * 
 * Features:
 * - Cross-session analytics and performance visualization
 * - Drawer + Vertical Tabs navigation (Settings-style)
 * - Persistent sidebar on desktop when pinned
 * - Mobile-responsive drawer behavior
 * - Professional analytics dashboard framework
 * - AI-powered behavioral analysis integration
 */
export const AnalysisHub: React.FC = () => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const scrollRef = useRef<ScrollShadowRef>(null);
  
  const [selectedSection, setSelectedSection] = useState<AnalysisSection>('overview');
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    // Default to pinned on desktop, unpinned on mobile (SSR-safe)
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });

  // Desktop detection
  const isDesktop = useIsDesktop();

  const handleSectionChange = useCallback((key: React.Key): void => {
    setSelectedSection(String(key) as AnalysisSection);
    // Auto-close drawer if not pinned
    if (!isPinned && isOpen) {
      onOpenChange();
    }
    // Scroll to top of analysis content
    if (scrollRef.current) {
      scrollRef.current.scrollToTop();
    }
  }, [isPinned, isOpen, onOpenChange]);

  const handlePinToggle = useCallback((): void => {
    setIsPinned(prev => !prev);
  }, []);

  // On mobile, pinned state should still allow drawer to close
  const shouldShowPersistent = isPinned && isDesktop;

  const analysisSections = [
    {
      key: 'overview',
      title: 'Overview',
      description: 'Analytics dashboard with key metrics and navigation'
    },
    {
      key: 'sessions',
      title: 'Sessions',
      description: 'Cross-session performance analysis and trends'
    },
    {
      key: 'trades',
      title: 'Trades',
      description: 'Individual trade analysis and pattern recognition'
    },
    {
      key: 'charts',
      title: 'Charts',
      description: 'Visual performance trends and statistical charts'
    },
    {
      key: 'ai-insights',
      title: 'AI Insights',
      description: 'Cross-session behavioral analysis and recommendations'
    },
    {
      key: 'reports',
      title: 'Reports',
      description: 'Standard trading performance reports and exports'
    }
  ];


  return (
    <AppLayout
      showBackButton
      backHref="/dashboard"
      backLabel="Back to Dashboard"
      headerContent={<AppSyncStatus />}
    >
      <div className="flex flex-row h-full ">
          {/* Persistent Sidebar (Desktop + Pinned) */}
          {shouldShowPersistent && (
            <div className="w-80 border-r  border-default-200 bg-transparent flex-shrink-0 h-full">
              <div className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Analysis Navigation</h2>
                  <Button
                    variant="light"
                    size="sm"
                    isIconOnly
                    onPress={handlePinToggle}
                    className="text-text-secondary"
                  >
                    <PinOff size={16} />
                  </Button>
                </div>
                <div className="flex-1 min-h-0">
                  <Tabs
                    isVertical
                    selectedKey={selectedSection}
                    onSelectionChange={handleSectionChange}
                    variant="light"
                    color="primary"
                    className="w-full h-full"
                    classNames={{
                      tabList: "w-full gap-2 h-full",
                      tab: "w-full justify-start p-4 h-auto",
                      tabContent: "w-full text-left",
                      cursor: "bg-default-200"
                    }}
                  >
                    {analysisSections.map((section) => (
                      <Tab 
                        key={section.key} 
                        title={
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium text-text-primary">{section.title}</span>
                            <span className="text-sm text-text-secondary text-wrap">
                              {section.description}
                            </span>
                          </div>
                        }
                      />
                    ))}
                  </Tabs>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Drawer (Mobile or Desktop Unpinned) */}
          {!shouldShowPersistent && (
            <Drawer 
              isOpen={isOpen} 
              onOpenChange={onOpenChange}
              placement="right"
              size="md"
              className="md:size-lg"
            >
              <DrawerContent>
                <DrawerHeader>
                  <div className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Analysis Navigation</h2>
                    {isDesktop && (
                      <Button
                        variant="light"
                        size="sm"
                        isIconOnly
                        onPress={handlePinToggle}
                        className="text-text-secondary mr-4"
                      >
                        <Pin size={16} />
                      </Button>
                    )}
                  </div>
                </DrawerHeader>
                <DrawerBody className="p-0">
                  <Tabs
                    isVertical
                    selectedKey={selectedSection}
                    onSelectionChange={handleSectionChange}
                    variant="light"
                    color="primary"
                    className="w-full"
                    classNames={{
                      tabList: "w-full gap-2 px-4",
                      tab: "w-full justify-start p-4 h-auto",
                      tabContent: "w-full text-left",
                      cursor: "bg-default-200"
                    }}
                  >
                    {analysisSections.map((section) => (
                      <Tab 
                        key={section.key} 
                        title={
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium text-text-primary">{section.title}</span>
                            <span className="text-sm text-text-secondary text-wrap">
                              {section.description}
                            </span>
                          </div>
                        }
                      />
                    ))}
                  </Tabs>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          )}

          {/* Main Analysis Content */}
          <div className="flex-1 min-h-0 p-4 box-border overflow-x-hidden">
            <AnalysisLayout
              title={analysisSections.find(s => s.key === selectedSection)?.title || 'Analysis Hub'}
              description={analysisSections.find(s => s.key === selectedSection)?.description || ''}
              scrollRef={scrollRef}
              {...(!shouldShowPersistent && { onOpenDrawer: onOpen })}
            >
              <AnalysisPlaceholder 
                section={selectedSection}
                sectionLabel={analysisSections.find(s => s.key === selectedSection)?.title || 'Analysis'}
              />
            </AnalysisLayout>
        </div>
      </div>
    </AppLayout>
  );
};